import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DismissCircleRegular } from '@fluentui/react-icons';
import { Box, IconButton, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridOverlay, useGridApiRef } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { truncateText } from '@ui/constants/truncateText';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSDataGridStyles from './useSDataGridStyles';
import { toast } from 'react-toastify';
export const SDataGrid = ({ columns, rows, onRowClick, onFilterChange, filterLoading, loading, totalRows, paginationModel, onPaginationModelChange, serverSidePagination = false, maxCharacter = 30, showFilters = false, pageSizeOptions, initialState, disabledColumns, hideColumns, 
// filters,
// setFilters,
getRowClassName, sortingMode, sortModel, onSortModelChange, sortingOrder }) => {
    const { t } = useTranslation();
    const { filterSearchStyles } = useSDataGridStyles();
    const [filters, setFilters] = useState(columns.reduce((acc, item) => {
        acc[item.field] = '';
        return acc;
    }, { search: '' }));
    const debounceTimerRef = useRef(null);
    const pendingFiltersRef = useRef(null);
    useEffect(() => {
        const initialFilters = columns.reduce((acc, item) => {
            acc[item.field] = '';
            return acc;
        }, { search: '' });
        setFilters(initialFilters);
    }, []);
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const handleColumnVisibilityModelChange = (model) => {
        setColumnVisibilityModel(model);
    };
    useEffect(() => {
        if (!hideColumns || hideColumns.length < 1)
            return;
        const columnVisibilityModel = hideColumns.reduce((acc, item) => {
            acc[item] = false;
            return acc;
        }, {});
        setColumnVisibilityModel(columnVisibilityModel);
    }, [hideColumns]);
    const isColumnVisible = useCallback((fieldId) => {
        return columnVisibilityModel[fieldId] !== false;
    }, [columnVisibilityModel]);
    const validateDateRange = useCallback((field, isFromDate) => {
        const fromKey = field + '_from';
        const toKey = field + '_to';
        if (!isFromDate && !filters[fromKey]) {
            toast.warning(t('PleaseSelectFromDateFirst'));
            return false;
        }
        return true;
    }, [filters, t]);
    const checkIncompleteFromDate = useCallback((field) => {
        const fromKey = field + '_from';
        const toKey = field + '_to';
        if (filters[fromKey] && !filters[toKey]) {
            toast.warning(t('PleaseSelectToDate'));
            return false;
        }
        return true;
    }, [filters, t]);
    const handleChange = useCallback((field) => (event) => {
        const dateFields = columns
            .filter((c) => c.type === 'date' || c.type === 'dateTime')
            .map((c) => c.field);
        for (const dateField of dateFields) {
            if (!checkIncompleteFromDate(dateField)) {
                event.preventDefault();
                event.target.blur();
                return;
            }
        }
        let value = event.target.value;
        const isScoreField = field === 'score_min' || field === 'score_max';
        const isTextField = !isScoreField && !dateFields.some((df) => field.includes(df));
        if (isScoreField && value !== '') {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
                if (numValue < 0) {
                    toast.warning(t('Score must be between 0 and 100'));
                    value = '';
                }
                else if (numValue > 100) {
                    toast.warning(t('Score must be between 0 and 100'));
                    value = '100';
                }
            }
        }
        const newFilters = {
            ...filters,
            [field]: value
        };
        setFilters(newFilters);
        if (isScoreField || isTextField) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            pendingFiltersRef.current = newFilters;
            debounceTimerRef.current = setTimeout(() => {
                if (pendingFiltersRef.current && onFilterChange) {
                    onFilterChange(pendingFiltersRef.current);
                    pendingFiltersRef.current = null;
                }
            }, 1000);
        }
        else {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            if (pendingFiltersRef.current) {
                if (onFilterChange)
                    onFilterChange(pendingFiltersRef.current);
                pendingFiltersRef.current = null;
            }
            if (onFilterChange)
                onFilterChange(newFilters);
        }
    }, [filters, onFilterChange, columns, checkIncompleteFromDate, t]);
    const handleSelectChange = useCallback((field) => (event) => {
        const dateFields = columns
            .filter((c) => c.type === 'date' || c.type === 'dateTime')
            .map((c) => c.field);
        for (const dateField of dateFields) {
            if (!checkIncompleteFromDate(dateField)) {
                event.preventDefault();
                return;
            }
        }
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        if (pendingFiltersRef.current) {
            if (onFilterChange)
                onFilterChange(pendingFiltersRef.current);
            pendingFiltersRef.current = null;
        }
        const newFilters = {
            ...filters,
            [field]: event.target.value
        };
        setFilters(newFilters);
        if (onFilterChange)
            onFilterChange(newFilters);
    }, [filters, onFilterChange, columns, checkIncompleteFromDate]);
    const clearSelect = useCallback((field) => {
        const newFilters = {
            ...filters,
            [field]: ''
        };
        setFilters(newFilters);
        if (onFilterChange)
            onFilterChange(newFilters); // Notify parent about the filter change
    }, [filters, onFilterChange]);
    const prevShowFiltersRef = useRef(showFilters);
    useEffect(() => {
        const wasShowing = prevShowFiltersRef.current;
        const isNowHiding = wasShowing && !showFilters;
        if (isNowHiding) {
            const dateFields = columns
                .filter((c) => c.type === 'date' || c.type === 'dateTime')
                .map((c) => c.field);
            const clearedFilters = columns.reduce((acc, item) => {
                if (item.field) {
                    acc[item.field] = '';
                }
                if (dateFields.includes(item.field)) {
                    acc[item.field + '_from'] = '';
                    acc[item.field + '_to'] = '';
                }
                return acc;
            }, { search: '' });
            setFilters((prevFilters) => {
                const allClearedFilters = { ...clearedFilters };
                Object.keys(prevFilters).forEach((key) => {
                    if (!allClearedFilters.hasOwnProperty(key)) {
                        allClearedFilters[key] = '';
                    }
                });
                if (onFilterChange) {
                    onFilterChange(allClearedFilters);
                }
                return allClearedFilters;
            });
        }
        prevShowFiltersRef.current = showFilters;
    }, [showFilters]);
    const apiRef = useGridApiRef();
    const scrollContainerRef = useRef(null);
    const handleEvent = (params) => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = params.left;
        }
    };
    useEffect(() => {
        const unsubscribe = apiRef.current.subscribeEvent('scrollPositionChange', handleEvent);
        return () => {
            unsubscribe();
        };
    }, [apiRef]);
    const theme = useTheme();
    const isSm = theme.breakpoints.down('sm');
    const isLight = theme.palette.mode === 'light';
    const columnsWithFilters = useMemo(() => columns.map((item, index) => ({
        ...item,
        renderHeader: (params) => (_jsxs(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                flex: item.flex,
                py: 1.5,
                gap: 1
            }, onClick: (event) => event?.stopPropagation(), children: [_jsx(Tooltip, { title: item.headerName, placement: 'top-start', children: _jsx(Typography, { variant: 'inherit', color: 'textSecondary', sx: {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            ...theme.typography.button
                        }, children: item.headerName }) }), showFilters && (_jsx(Box, { display: isColumnVisible(item.field) ? 'block' : 'none', children: item.type === 'singleSelect' ? (_jsx(Select, { fullWidth: true, size: 'small', sx: { bgcolor: '#919EAB0A', pr: 0 }, value: filters[item.field] || '', onChange: handleSelectChange(item.field), onClick: (e) => {
                            const dateFields = columns
                                .filter((c) => c.type === 'date' || c.type === 'dateTime')
                                .map((c) => c.field);
                            for (const dateField of dateFields) {
                                if (!checkIncompleteFromDate(dateField)) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                }
                            }
                        }, onOpen: (e) => {
                            const dateFields = columns
                                .filter((c) => c.type === 'date' || c.type === 'dateTime')
                                .map((c) => c.field);
                            for (const dateField of dateFields) {
                                if (!checkIncompleteFromDate(dateField)) {
                                    e.preventDefault();
                                    return;
                                }
                            }
                        }, disabled: disabledColumns ? disabledColumns.includes(item.field) : false, endAdornment: filters[item.field] && (_jsx(InputAdornment, { position: 'end', sx: { marginRight: '4px' }, children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                                    e.stopPropagation();
                                    clearSelect(item.field);
                                }, children: _jsx(DismissCircleRegular, { fontSize: 20 }) }) })), IconComponent: filters[item.field] ? () => null : undefined, MenuProps: {
                            PaperProps: {
                                sx: { maxHeight: isSm ? 200 : 300 }
                            },
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            }
                        }, children: item.valueOptions.map((option) => {
                            const value = typeof option === 'object' ? option.value : option;
                            const label = typeof option === 'object' ? option.label : option;
                            return (_jsx(MenuItem, { value: value, title: label, children: truncateText(label, maxCharacter) }, value));
                        }) })) : item.type === 'actions' ? (_jsx(Box, { width: 100 })) : item.type === 'date' || item.type === 'dateTime' ? (item.field === 'incidentDate' ? (_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(DatePicker, { slotProps: { textField: { size: 'small' } }, format: 'DD/MM/YYYY', value: filters[item.field + '_from'] &&
                                        dayjs(filters[item.field + '_from']).isValid()
                                        ? dayjs(filters[item.field + '_from'])
                                        : null, disabled: disabledColumns?.includes(item.field), onChange: (newValue) => {
                                        const formattedDate = newValue && dayjs(newValue).isValid()
                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                            : '';
                                        if (debounceTimerRef.current) {
                                            clearTimeout(debounceTimerRef.current);
                                            debounceTimerRef.current = null;
                                        }
                                        if (pendingFiltersRef.current) {
                                            if (onFilterChange)
                                                onFilterChange(pendingFiltersRef.current);
                                            pendingFiltersRef.current = null;
                                        }
                                        const newFilters = {
                                            ...filters,
                                            [item.field + '_from']: formattedDate
                                        };
                                        setFilters(newFilters);
                                        if (onFilterChange)
                                            onFilterChange(newFilters);
                                    } }), _jsx("span", { children: "-" }), _jsx(DatePicker, { slotProps: {
                                        textField: {
                                            size: 'small',
                                            onClick: (e) => {
                                                if (!filters[item.field + '_from']) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toast.warning(t('PleaseSelectFromDateFirst'));
                                                    return false;
                                                }
                                            },
                                            onFocus: (e) => {
                                                if (!filters[item.field + '_from']) {
                                                    e.preventDefault();
                                                    e.target.blur();
                                                    toast.warning(t('PleaseSelectFromDateFirst'));
                                                }
                                            }
                                        },
                                        openPickerIcon: {
                                            onClick: (e) => {
                                                if (!filters[item.field + '_from']) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toast.warning(t('PleaseSelectFromDateFirst'));
                                                }
                                            }
                                        }
                                    }, format: 'DD/MM/YYYY', value: filters[item.field + '_to'] &&
                                        dayjs(filters[item.field + '_to']).isValid()
                                        ? dayjs(filters[item.field + '_to'])
                                        : null, disabled: disabledColumns?.includes(item.field) || !filters[item.field + '_from'], onOpen: () => {
                                        if (!filters[item.field + '_from']) {
                                            toast.warning(t('PleaseSelectFromDateFirst'));
                                        }
                                    }, onChange: (newValue) => {
                                        if (!filters[item.field + '_from']) {
                                            toast.warning(t('PleaseSelectFromDateFirst'));
                                            return;
                                        }
                                        const formattedDate = newValue && dayjs(newValue).isValid()
                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                            : '';
                                        if (debounceTimerRef.current) {
                                            clearTimeout(debounceTimerRef.current);
                                            debounceTimerRef.current = null;
                                        }
                                        if (pendingFiltersRef.current) {
                                            if (onFilterChange)
                                                onFilterChange(pendingFiltersRef.current);
                                            pendingFiltersRef.current = null;
                                        }
                                        const newFilters = {
                                            ...filters,
                                            [item.field + '_to']: formattedDate
                                        };
                                        setFilters(newFilters);
                                        if (onFilterChange)
                                            onFilterChange(newFilters);
                                    } })] }) })) : (_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(DatePicker, { slotProps: { textField: { size: 'small' } }, format: 'DD/MM/YYYY', value: filters[item.field] && dayjs(filters[item.field]).isValid()
                                ? dayjs(filters[item.field])
                                : null, disabled: disabledColumns?.includes(item.field), onChange: (newValue) => {
                                const formattedDate = newValue && dayjs(newValue).isValid() ? newValue : null;
                                const newFilters = {
                                    ...filters,
                                    [item.field]: formattedDate
                                };
                                setFilters(newFilters);
                                if (onFilterChange)
                                    onFilterChange(newFilters);
                            } }) }))) : item.field !== 'score' ? (_jsx(TextField, { sx: filterSearchStyles, fullWidth: true, size: 'small', type: item.type || 'text', value: filters[item.field] || '', onChange: handleChange(item.field), onClick: (e) => {
                            e.stopPropagation();
                            const dateFields = columns
                                .filter((c) => c.type === 'date' || c.type === 'dateTime')
                                .map((c) => c.field);
                            for (const dateField of dateFields) {
                                if (!checkIncompleteFromDate(dateField)) {
                                    e.preventDefault();
                                    e.target.blur();
                                    return;
                                }
                            }
                        }, onFocus: (e) => {
                            const dateFields = columns
                                .filter((c) => c.type === 'date' || c.type === 'dateTime')
                                .map((c) => c.field);
                            for (const dateField of dateFields) {
                                if (!checkIncompleteFromDate(dateField)) {
                                    e.preventDefault();
                                    e.target.blur();
                                    return;
                                }
                            }
                        }, disabled: disabledColumns ? disabledColumns.includes(item.field) : false })) : (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(TextField, { sx: filterSearchStyles, size: 'small', type: 'number', inputProps: { min: 0, max: 100 }, value: filters[item.field + '_min'] || '', onChange: handleChange(item.field + '_min'), onBlur: (e) => {
                                    const value = e.target.value;
                                    if (value !== '') {
                                        const numValue = Number(value);
                                        if (isNaN(numValue) || numValue < 0) {
                                            e.target.value = '';
                                            setFilters((prev) => {
                                                const updated = { ...prev, [item.field + '_min']: '' };
                                                if (onFilterChange)
                                                    onFilterChange(updated);
                                                return updated;
                                            });
                                        }
                                        else if (numValue > 100) {
                                            e.target.value = '100';
                                            setFilters((prev) => {
                                                const updated = { ...prev, [item.field + '_min']: '100' };
                                                if (onFilterChange)
                                                    onFilterChange(updated);
                                                return updated;
                                            });
                                        }
                                    }
                                }, onClick: (e) => e.stopPropagation(), disabled: disabledColumns ? disabledColumns.includes(item.field) : false }), _jsx("span", { children: "-" }), _jsx(TextField, { sx: filterSearchStyles, size: 'small', type: 'number', inputProps: { min: 0, max: 100 }, value: filters[item.field + '_max'] || '', onChange: handleChange(item.field + '_max'), onBlur: (e) => {
                                    const value = e.target.value;
                                    if (value !== '') {
                                        const numValue = Number(value);
                                        if (isNaN(numValue) || numValue < 0) {
                                            e.target.value = '';
                                            setFilters((prev) => {
                                                const updated = { ...prev, [item.field + '_max']: '' };
                                                if (onFilterChange)
                                                    onFilterChange(updated);
                                                return updated;
                                            });
                                        }
                                        else if (numValue > 100) {
                                            e.target.value = '100';
                                            setFilters((prev) => {
                                                const updated = { ...prev, [item.field + '_max']: '100' };
                                                if (onFilterChange)
                                                    onFilterChange(updated);
                                                return updated;
                                            });
                                        }
                                    }
                                }, onClick: (e) => e.stopPropagation(), disabled: disabledColumns ? disabledColumns.includes(item.field) : false })] })) }, index))] }))
    })), [
        checkIncompleteFromDate,
        clearSelect,
        columns,
        disabledColumns,
        filterSearchStyles,
        filters,
        handleChange,
        handleSelectChange,
        isColumnVisible,
        isSm,
        maxCharacter,
        onFilterChange,
        showFilters,
        t,
        theme.typography.button
    ]);
    return (_jsx(Box, { sx: {
            width: '100%',
            height: '100%',
            overflow: 'auto',
            background: isLight ? 'white' : undefined,
            position: 'relative'
        }, children: _jsx(DataGrid, { apiRef: apiRef, rows: rows || [], columns: columnsWithFilters, getRowId: (row) => row.id, paginationMode: serverSidePagination ? 'server' : 'client', sortingMode: sortingMode, sortingOrder: sortingOrder, sortModel: sortModel, onSortModelChange: onSortModelChange, rowCount: serverSidePagination ? (totalRows ?? 0) : undefined, pageSizeOptions: pageSizeOptions, paginationModel: paginationModel, onPaginationModelChange: onPaginationModelChange, onColumnVisibilityModelChange: handleColumnVisibilityModelChange, columnVisibilityModel: columnVisibilityModel, hideFooter: serverSidePagination ? false : true, disableColumnMenu: true, hideFooterPagination: serverSidePagination ? false : true, hideFooterSelectedRowCount: serverSidePagination ? false : true, disableColumnFilter: serverSidePagination ? false : true, initialState: initialState, getRowClassName: getRowClassName, slots: {
                noRowsOverlay: () => {
                    return (_jsx(GridOverlay, { children: _jsx(Typography, { variant: 'subtitle2', color: 'text.secondary', children: t('noRecordsFound') }) }));
                },
                loadingOverlay: () => (_jsx(GridOverlay, { children: _jsx("img", { src: '/assets/svgs/loader.svg', alt: 'loader' }) }))
            }, loading: filterLoading || loading, sx: {
                width: '100%',
                '& .MuiDataGrid-columnHeader': {
                    height: showFilters ? 'auto !important' : 56
                },
                '@keyframes highlightFade': {
                    '0%': {
                        backgroundColor: '#8E33FF'
                    },
                    '50%': {
                        backgroundColor: '#aa66ff'
                    },
                    '100%': {
                        backgroundColor: '#8E33FF'
                    }
                },
                '& .MuiDataGrid-row.highlight-inline': {
                    backgroundColor: 'rgba(218, 155, 252, 0.5)',
                    transition: 'background-color 0.5s ease-in-out'
                }
            }, onRowClick: onRowClick }, rows && rows.length > 0 ? `grid-${rows[0]?.id}` : 'grid-empty') }));
};
